import { ResponseError } from '@evimo/client/runtime';
import { buildApiUrl } from '~/app/lib/api-config';
import type {
  ExportBareLogsFilter,
  PreviewBareLogsFilter,
} from '@evimo/types/api/goldenRecord/export-bare-logs/export-bare-logs.request.dto';
import type {
  PaginatedBareLogs,
  PaginatedOrganizationBareLogs,
} from '@evimo/types/api/goldenRecord/export-bare-logs/export-bare-logs.response.dto';
import type { FindAssetLogsFilter } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.request.dto';
import type { PaginatedAssetLogs } from '@evimo/types/api/goldenRecord/find-asset-logs/find-asset-logs.response.dto';
import type { UpdateAssetLogRequest } from '@evimo/types/api/goldenRecord/update-asset-log/update-asset-log.request.dto';
import {
  assetLogsExportApi,
  assetLogsRequestInit,
  normalizeLogsAtRestApi,
  organizationLogsExportApi,
} from '~/app/services/goldenRecord/asset-logs-api-instances';

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function readErrorMessage(response: Response, fallback: string) {
  const errorPayload = await response
    .json()
    .catch(() => ({ message: response.statusText }));

  return errorPayload.message || fallback;
}

async function throwLogsApiError(
  error: unknown,
  fallback: string,
): Promise<never> {
  if (error instanceof ResponseError) {
    throw new Error(await readErrorMessage(error.response, fallback));
  }
  throw error instanceof Error ? error : new Error(fallback);
}

function assetLogsBasePath(organizationId: string, assetId: string): string {
  return `/api/v1/organizations/${organizationId}/assets/${assetId}`;
}

export const AssetLogsService = {
  async findPaginated(
    organizationId: string,
    assetId: string,
    filter: FindAssetLogsFilter,
  ): Promise<PaginatedAssetLogs> {
    try {
      return (await normalizeLogsAtRestApi.goldenRecordStreamControllerGetLogsForAsset(
        {
          organizationId,
          assetId,
          ...(filter.limit != null ? { limit: filter.limit } : {}),
          ...(filter.cursor ? { cursor: filter.cursor } : {}),
          ...(filter.from ? { from: filter.from } : {}),
          ...(filter.to ? { to: filter.to } : {}),
          ...(filter.isTreated != null ? { isTreated: filter.isTreated } : {}),
        },
        assetLogsRequestInit,
      )) as unknown as PaginatedAssetLogs;
    } catch (error) {
      return throwLogsApiError(error, 'Failed to load asset logs');
    }
  },

  async update(
    organizationId: string,
    assetId: string,
    logId: string,
    payload: UpdateAssetLogRequest,
  ): Promise<void> {
    try {
      await normalizeLogsAtRestApi.goldenRecordStreamControllerUpdateLogForAsset(
        {
          organizationId,
          assetId,
          logId,
          updateAssetLogRequestDto: payload as never,
        },
        assetLogsRequestInit,
      );
    } catch (error) {
      return throwLogsApiError(error, 'Failed to update asset log');
    }
  },

  async normalizeLogs(
    organizationId: string,
    assetId: string,
    payload: {
      clientName: string;
      fromDate: string;
      toDate: string;
      batchSize: number;
      isTreated: boolean;
    },
  ): Promise<void> {
    try {
      await normalizeLogsAtRestApi.goldenRecordStreamControllerStreamForAsset(
        {
          organizationId,
          assetId,
          streamGoldenRecordsRequestDto: {
            clientName: payload.clientName,
            fromDate: payload.fromDate,
            toDate: payload.toDate,
            batchSize: payload.batchSize,
            treated: payload.isTreated,
          },
        },
        assetLogsRequestInit,
      );
    } catch (error) {
      return throwLogsApiError(error, 'Failed to normalize logs');
    }
  },

  async previewBareLogs(
    organizationId: string,
    assetId: string,
    filter: PreviewBareLogsFilter,
  ): Promise<PaginatedBareLogs> {
    try {
      return (await assetLogsExportApi.assetBareLogsExportControllerPreviewAssetLogs(
        {
          organizationId,
          assetId,
          from: filter.from,
          to: filter.to,
          page: filter.page ?? 1,
          limit: filter.limit ?? 20,
        },
        assetLogsRequestInit,
      )) as unknown as PaginatedBareLogs;
    } catch (error) {
      return throwLogsApiError(error, 'Failed to preview bare logs');
    }
  },

  async previewOrganizationBareLogs(
    organizationId: string,
    filter: PreviewBareLogsFilter,
  ): Promise<PaginatedOrganizationBareLogs> {
    try {
      return (await organizationLogsExportApi.organizationBareLogsExportControllerPreviewOrganizationLogs(
        {
          organizationId,
          from: filter.from,
          to: filter.to,
          page: filter.page ?? 1,
          limit: filter.limit ?? 20,
        },
        assetLogsRequestInit,
      )) as unknown as PaginatedOrganizationBareLogs;
    } catch (error) {
      return throwLogsApiError(
        error,
        'Failed to preview organization bare logs',
      );
    }
  },

  /**
   * Direct fetch retained: generated export APIs return void and do not expose
   * the binary blob / Content-Disposition filename needed for downloads.
   */
  async downloadBareLogs(
    organizationId: string,
    assetId: string,
    filter: ExportBareLogsFilter,
  ): Promise<void> {
    const params = new URLSearchParams({
      from: filter.from,
      to: filter.to,
    });

    const response = await fetch(
      buildApiUrl(
        `${assetLogsBasePath(organizationId, assetId)}/logs/export?${params.toString()}`,
      ),
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, 'Failed to download bare logs'),
      );
    }

    const blob = await response.blob();
    const contentDisposition =
      response.headers.get('Content-Disposition') ?? '';
    const filenameMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    const filename = filenameMatch?.[1] ?? `asset-${assetId}-bare-logs.json`;

    triggerBlobDownload(blob, filename);
  },

  /**
   * Direct fetch retained: generated export APIs return void and do not expose
   * the binary blob / Content-Disposition filename needed for downloads.
   */
  async downloadOrganizationBareLogs(
    organizationId: string,
    filter: ExportBareLogsFilter,
  ): Promise<void> {
    const params = new URLSearchParams({
      from: filter.from,
      to: filter.to,
    });

    const response = await fetch(
      buildApiUrl(
        `/api/v1/organizations/${organizationId}/logs/export?${params.toString()}`,
      ),
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          'Failed to download organization logs',
        ),
      );
    }

    const blob = await response.blob();
    const contentDisposition =
      response.headers.get('Content-Disposition') ?? '';
    const filenameMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    const filename =
      filenameMatch?.[1] ?? `organization-${organizationId}-bare-logs.zip`;

    triggerBlobDownload(blob, filename);
  },
};
